/**
 * Usuarios y Roles — migración web de seg9020/seg9030/seg9060.
 *
 * Pestaña "Usuarios": listado de la tabla usuarios con alta (usuario + clave,
 * replicada en usuariosnube por el backend) y edición de rol/sucursal/web/
 * local/email. Incluye "Cambiar mi clave" (seg9030), que verifica la clave
 * actual; tras el cambio hay que usar la clave nueva en el próximo login.
 * Pestaña "Roles" (estilo Bankivo): lista de roles como entidades — avatar,
 * cuántos usuarios lo tienen, chips con los módulos que cubre — con
 * Crear / Editar (asistenterol.js, marca pantallas del catálogo
 * catalogoProgramas.js) y Eliminar rol (bloqueado si tiene usuarios).
 * "Vista avanzada" conserva la grilla plana de filas de rolesusuarios con
 * el alta/edición fila a fila; su campo de pantalla autocompleta programa /
 * descripción / categoría desde el mismo catálogo.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PhonelinkLockOutlinedIcon from "@mui/icons-material/PhonelinkLockOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { getCatalogoProgramas, getEntradaPrograma } from "lib/seguridad/catalogoProgramas";
import AsistenteRol from "modules/configuracion/asistenterol";
import { PROGRAMA_BLOQUEO_MOVIL } from "lib/dispositivo";
import CambiarClaveDialog from "modules/menu/CambiarClaveDialog";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin registros</Typography>
    </Stack>
  );
}

const usuarioVacio = {
  nombre: "",
  clave: "",
  tipo: "",
  sucursal: "",
  web: "S",
  local: "S",
  email: "",
};

const rolVacio = {
  rol: "",
  programa: "",
  descripcion: "",
  categoria: "",
  modificar: "N",
  borrar: "N",
  versucursal: "N",
  insertar: "N",
};

const FLAGS_ROL = [
  { campo: "modificar", label: "Modificar" },
  { campo: "borrar", label: "Borrar" },
  { campo: "versucursal", label: "Ver sucursal" },
  { campo: "insertar", label: "Insertar" },
];

// `vista` fija la pantalla en una sola pestaña ("usuarios" | "roles") y
// oculta las Tabs — así el hub de Configuración las ofrece como dos
// entradas separadas del menú lateral. Sin `vista` se comporta como
// siempre (ambas pestañas), que es lo que usan las URLs viejas.
export default function UsuariosRoles({ enHub, vista } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  // Con `vista` la pestaña se DERIVA del prop en cada render: el hub reutiliza
  // esta misma instancia para "Usuarios" y "Roles" (mismo tipo de componente),
  // así que un useState inicializado desde el prop se quedaría en la pestaña
  // del primer montaje al cambiar de entrada en el menú lateral.
  const [tabLibre, setTabLibre] = useState(0);
  const tab = { usuarios: 0, roles: 1 }[vista] ?? tabLibre;

  // ---- Usuarios ----
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);

  const cargarUsuarios = async () => {
    setCargandoUsuarios(true);
    try {
      const { ok, data } = await post("/api/config_usuarios", {});
      if (ok) {
        setUsuarios((data.usuarios || []).map((r) => ({ ...r, id: r.usuario })));
        setRoles(data.roles || []);
      } else {
        showAlert({
          message: data.Error || "No se pudieron listar los usuarios",
          color: "error",
        });
      }
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // Sucursales para el selector del formulario de usuario. El valor que se
  // guarda es el código (lo que hay antes del guion en "2-Veron"), igual que
  // almacena la tabla usuarios.
  const sucursales = useSucursales();
  const opcionesSucursal = sucursales.map((s) => {
    const label = String(s.label || "").trim();
    return { codigo: label.split("-")[0].trim(), label };
  });

  const [editandoUsuario, setEditandoUsuario] = useState(null); // null, 0=nuevo, usuario=editar
  const [formUsuario, setFormUsuario] = useState(usuarioVacio);
  const [guardandoUsuario, setGuardandoUsuario] = useState(false);

  const setCampoUsuario = (k) => (e) => setFormUsuario((f) => ({ ...f, [k]: e.target.value }));

  const abrirNuevoUsuario = () => {
    setFormUsuario(usuarioVacio);
    setEditandoUsuario(0);
  };

  const abrirEditarUsuario = (row) => {
    setFormUsuario({
      nombre: row.usuario || "",
      clave: "",
      tipo: row.tipo || "",
      sucursal: String(row.sucursal ?? ""),
      web: row.web === "S" ? "S" : "N",
      local: row.local === "S" ? "S" : "N",
      email: row.email || "",
    });
    setEditandoUsuario(row.usuario);
  };

  const guardarUsuario = async () => {
    const esNuevo = editandoUsuario === 0;
    if (!formUsuario.nombre.trim()) {
      showAlert({ message: "El usuario no puede estar en blanco", color: "warning" });
      return;
    }
    if (esNuevo && formUsuario.clave.trim().length < 5) {
      showAlert({ message: "La clave debe tener al menos 5 caracteres", color: "warning" });
      return;
    }
    if (!formUsuario.tipo.trim()) {
      showAlert({ message: "Debe seleccionar un rol", color: "warning" });
      return;
    }
    if (!formUsuario.sucursal.trim()) {
      showAlert({ message: "La sucursal no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardandoUsuario(true);
    try {
      const { ok, data } = await post("/api/config_usuario_guardar", {
        nuevo: esNuevo,
        nombre: formUsuario.nombre.trim(),
        clave: formUsuario.clave.trim(),
        tipo: formUsuario.tipo.trim(),
        sucursal: formUsuario.sucursal.trim(),
        web: formUsuario.web,
        local: formUsuario.local,
        email: formUsuario.email.trim(),
      });
      if (ok) {
        showAlert({
          message: esNuevo ? "Usuario registrado" : "Usuario modificado",
          color: "success",
        });
        setEditandoUsuario(null);
        cargarUsuarios();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el usuario", color: "error" });
      }
    } finally {
      setGuardandoUsuario(false);
    }
  };

  // ---- Filtro por sucursal y eliminación de usuarios ----
  const [filtroSucursal, setFiltroSucursal] = useState("");
  const [busquedaUsuario, setBusquedaUsuario] = useState("");
  // Códigos presentes en los usuarios (con la etiqueta del catálogo cuando
  // existe), así también aparecen sucursales que ya no están en el catálogo.
  const sucursalesUsuarios = [...new Set(usuarios.map((u) => String(u.sucursal ?? "").trim()))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((codigo) => ({
      codigo,
      label: codigo
        ? opcionesSucursal.find((s) => s.codigo === codigo)?.label || codigo
        : "(Sin sucursal)",
    }));
  // Búsqueda por nombre de usuario, rol o email (sin distinguir mayúsculas).
  const textoBusqueda = busquedaUsuario.trim().toLowerCase();
  const usuariosFiltrados = usuarios.filter(
    (u) =>
      (filtroSucursal === "" || String(u.sucursal ?? "").trim() === filtroSucursal) &&
      (!textoBusqueda ||
        [u.usuario, u.tipo, u.email].some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(textoBusqueda)
        ))
  );

  const [eliminandoUsuario, setEliminandoUsuario] = useState(null); // fila a eliminar o null
  const [borrandoUsuario, setBorrandoUsuario] = useState(false);

  const eliminarUsuario = async () => {
    setBorrandoUsuario(true);
    try {
      const { ok, data } = await post("/api/config_usuario_eliminar", {
        nombre: eliminandoUsuario.usuario,
      });
      if (ok) {
        showAlert({ message: `Usuario ${eliminandoUsuario.usuario} eliminado`, color: "success" });
        setEliminandoUsuario(null);
        cargarUsuarios();
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar el usuario", color: "error" });
      }
    } finally {
      setBorrandoUsuario(false);
    }
  };

  // ---- Cambiar mi clave ----
  const [cambiandoClave, setCambiandoClave] = useState(false);

  // ---- Roles ----
  const [filasRoles, setFilasRoles] = useState([]);
  const [filtroRol, setFiltroRol] = useState("");
  const [cargandoRoles, setCargandoRoles] = useState(false);

  const cargarRoles = async () => {
    setCargandoRoles(true);
    try {
      const { ok, data } = await post("/api/config_roles", {});
      if (ok) {
        setFilasRoles((data.filas || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los roles", color: "error" });
      }
    } finally {
      setCargandoRoles(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
    cargarRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editandoRol, setEditandoRol] = useState(null); // null, 0=nuevo, id=editar
  const [formRol, setFormRol] = useState(rolVacio);
  const [guardandoRol, setGuardandoRol] = useState(false);
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  const [rolAEditar, setRolAEditar] = useState(null); // rol abierto en el asistente
  const [vistaAvanzada, setVistaAvanzada] = useState(false);
  const catalogo = useMemo(() => getCatalogoProgramas(), []);

  const setCampoRol = (k) => (e) => setFormRol((f) => ({ ...f, [k]: e.target.value }));

  const abrirNuevoRol = () => {
    setFormRol({ ...rolVacio, rol: filtroRol });
    setEditandoRol(0);
  };

  const abrirEditarRol = (row) => {
    setFormRol({
      rol: row.rol || "",
      programa: row.programa || "",
      descripcion: row.descripcion || "",
      categoria: row.categoria || "",
      modificar: row.modificar === "S" ? "S" : "N",
      borrar: row.borrar === "S" ? "S" : "N",
      versucursal: row.versucursal === "S" ? "S" : "N",
      insertar: row.insertar === "S" ? "S" : "N",
    });
    setEditandoRol(row.id);
  };

  const guardarRol = async () => {
    if (!formRol.rol.trim() || !formRol.programa.trim()) {
      showAlert({ message: "Rol y programa son obligatorios", color: "warning" });
      return;
    }
    setGuardandoRol(true);
    try {
      const { ok, data } = await post("/api/config_rol_guardar", {
        rol: formRol.rol.trim(),
        programa: formRol.programa.trim(),
        descripcion: formRol.descripcion.trim(),
        categoria: formRol.categoria.trim(),
        modificar: formRol.modificar,
        borrar: formRol.borrar,
        versucursal: formRol.versucursal,
        insertar: formRol.insertar,
      });
      if (ok) {
        showAlert({ message: "Rol guardado", color: "success" });
        setEditandoRol(null);
        cargarRoles();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el rol", color: "error" });
      }
    } finally {
      setGuardandoRol(false);
    }
  };

  const [eliminandoRol, setEliminandoRol] = useState(null); // fila a eliminar o null
  const [borrandoRol, setBorrandoRol] = useState(false);

  const eliminarRol = async () => {
    setBorrandoRol(true);
    try {
      const { ok, data } = await post("/api/config_rol_eliminar", { id: eliminandoRol.id });
      if (ok) {
        showAlert({ message: "Fila de rol eliminada", color: "success" });
        setEliminandoRol(null);
        cargarRoles();
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar la fila", color: "error" });
      }
    } finally {
      setBorrandoRol(false);
    }
  };

  const rolesDistintos = [...new Set(filasRoles.map((r) => r.rol))];
  const filasFiltradas = filtroRol ? filasRoles.filter((r) => r.rol === filtroRol) : filasRoles;

  // Resumen por rol para la lista (estilo Bankivo): cuántos usuarios lo
  // tienen y qué módulos cubre según el catálogo.
  const resumenRoles = useMemo(
    () =>
      rolesDistintos.map((rol) => {
        const todas = filasRoles.filter((f) => f.rol === rol);
        // La fila "bloqueomovil" no es una pantalla: marca que el rol no puede
        // entrar desde el celular. No cuenta como pantalla ni como Escritorio.
        const filaBloqueo = todas.find(
          (f) =>
            String(f.programa || "")
              .trim()
              .toLowerCase() === PROGRAMA_BLOQUEO_MOVIL
        );
        const filas = todas.filter((f) => f !== filaBloqueo);
        const modulos = [];
        let desconocidas = 0;
        filas.forEach((f) => {
          const entrada = getEntradaPrograma(f.programa);
          if (!entrada) {
            desconocidas += 1;
            return;
          }
          if (!modulos.includes(entrada.modulo)) modulos.push(entrada.modulo);
        });
        return {
          rol,
          pantallas: filas.length,
          modulos,
          desconocidas,
          filaBloqueo: filaBloqueo || null,
          usuarios: usuarios.filter((u) => (u.tipo || "").trim() === rol).length,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filasRoles, usuarios]
  );

  // Bloqueo de celular por rol: prende = crea la fila "bloqueomovil",
  // apaga = la borra. Lo aplica el backend al iniciar sesión.
  const [cambiandoBloqueo, setCambiandoBloqueo] = useState(null); // rol en curso
  const cambiarBloqueoMovil = async (r, bloquear) => {
    setCambiandoBloqueo(r.rol);
    try {
      const { ok, data } = bloquear
        ? await post("/api/config_rol_guardar", {
            rol: r.rol,
            programa: PROGRAMA_BLOQUEO_MOVIL,
            descripcion: "Bloqueo de acceso desde el celular",
            categoria: "seguridad",
          })
        : await post("/api/config_rol_eliminar", { id: r.filaBloqueo?.id });
      if (ok) {
        showAlert({
          message: bloquear
            ? `El rol ${r.rol} ya no puede entrar desde el celular`
            : `El rol ${r.rol} puede entrar desde el celular`,
          color: "success",
        });
        cargarRoles();
      } else {
        showAlert({ message: data?.Error || "No se pudo cambiar el bloqueo", color: "error" });
      }
    } catch (e) {
      showAlert({ message: "No se pudo cambiar el bloqueo", color: "error" });
    } finally {
      setCambiandoBloqueo(null);
    }
  };

  // Eliminación de un rol completo (todas sus filas, una a una).
  const [eliminandoRolNombre, setEliminandoRolNombre] = useState(null);
  const [borrandoRolCompleto, setBorrandoRolCompleto] = useState(false);

  const eliminarRolCompleto = async () => {
    const filas = filasRoles.filter((f) => f.rol === eliminandoRolNombre);
    setBorrandoRolCompleto(true);
    const errores = [];
    try {
      for (let i = 0; i < filas.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { ok } = await post("/api/config_rol_eliminar", { id: filas[i].id });
          if (!ok) errores.push(filas[i].programa);
        } catch (e) {
          errores.push(filas[i].programa);
        }
      }
    } finally {
      setBorrandoRolCompleto(false);
    }
    if (errores.length) {
      showAlert({
        message: `El rol quedó a medias; no se pudieron eliminar ${errores.length} filas. Vuelva a intentar.`,
        color: "error",
      });
    } else {
      showAlert({ message: `Rol ${eliminandoRolNombre} eliminado`, color: "success" });
      if (filtroRol === eliminandoRolNombre) setFiltroRol("");
    }
    setEliminandoRolNombre(null);
    cargarRoles();
  };

  const columnasUsuarios = [
    { field: "usuario", headerName: "Usuario", flex: 1, minWidth: 160 },
    { field: "tipo", headerName: "Rol", width: 140 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
    { field: "web", headerName: "Web", width: 80 },
    { field: "local", headerName: "Local", width: 80 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    {
      field: "acciones",
      headerName: "",
      width: 210,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => abrirEditarUsuario(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Editar
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setEliminandoUsuario(params.row)}
            sx={btnOutline("#B3261E")}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  const columnasRoles = [
    { field: "rol", headerName: "Rol", width: 120 },
    {
      field: "programa",
      headerName: "Pantalla",
      width: 190,
      // El usuario no conoce los códigos ares: se muestra la etiqueta del
      // catálogo y solo queda el código para filas que no reconocemos.
      valueGetter: (value) => getEntradaPrograma(value)?.etiqueta || value,
    },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 200 },
    { field: "categoria", headerName: "Categoría", width: 130 },
    { field: "modificar", headerName: "Modificar", width: 90 },
    { field: "borrar", headerName: "Borrar", width: 80 },
    { field: "versucursal", headerName: "Ver suc.", width: 85 },
    { field: "insertar", headerName: "Insertar", width: 85 },
    {
      field: "acciones",
      headerName: "",
      width: 210,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => abrirEditarRol(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Editar
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setEliminandoRol(params.row)}
            sx={btnOutline("#B3261E")}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title={{ usuarios: "Usuarios", roles: "Roles" }[vista] || "Usuarios y Roles"}
        subtitle={
          {
            usuarios: "Cuentas de usuario del sistema",
            roles: "Roles y permisos por pantalla",
          }[vista] || "Usuarios del sistema y permisos por rol"
        }
        cta={
          vista === "roles" ? null : (
            <Button
              startIcon={<KeyOutlinedIcon />}
              onClick={() => setCambiandoClave(true)}
              sx={btnOutline(FP.color.navy)}
            >
              Cambiar mi clave
            </Button>
          )
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          {!vista && (
            <Tabs
              value={tab}
              onChange={(_, v) => setTabLibre(v)}
              sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
            >
              <Tab label="Usuarios" />
              <Tab label="Roles" />
            </Tabs>
          )}

          {tab === 0 && (
            <>
              <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
                <Grid item xs={12} sm="auto">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={abrirNuevoUsuario}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Nuevo usuario
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar usuario"
                    placeholder="Usuario, rol o email"
                    value={busquedaUsuario}
                    onChange={(e) => setBusquedaUsuario(e.target.value)}
                    sx={fpInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Sucursal"
                    value={filtroSucursal}
                    onChange={(e) => setFiltroSucursal(e.target.value)}
                    sx={fpInputSx}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {sucursalesUsuarios.map((s) => (
                      <MenuItem key={s.codigo || "_vacia"} value={s.codigo}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography
                    sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
                  >
                    {usuariosFiltrados.length === usuarios.length
                      ? `${usuarios.length} usuarios`
                      : `${usuariosFiltrados.length} de ${usuarios.length} usuarios`}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={usuariosFiltrados}
                  columns={columnasUsuarios}
                  loading={cargandoUsuarios}
                  rowHeight={46}
                  pageSizeOptions={[25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  slots={{ noRowsOverlay: NoRowsOverlay }}
                  sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}

          {tab === 1 && (
            <>
              <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
                <Grid item xs={6}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setAsistenteAbierto(true)}
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    Nuevo rol
                  </Button>
                </Grid>
                <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button onClick={() => setVistaAvanzada((v) => !v)} sx={btnGhost}>
                    {vistaAvanzada ? "Vista simple" : "Vista avanzada"}
                  </Button>
                </Grid>
              </Grid>

              {!vistaAvanzada && (
                <Box>
                  {cargandoRoles && <CircularProgress size={22} sx={{ my: 2 }} />}
                  {!cargandoRoles && !resumenRoles.length && (
                    <Stack alignItems="center" sx={{ py: 6, color: FP.color.textMuted }}>
                      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
                      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
                        Sin roles; cree el primero con &quot;Nuevo rol&quot;
                      </Typography>
                    </Stack>
                  )}
                  {resumenRoles.map((r, idx) => (
                    <React.Fragment key={r.rol}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "flex-start", sm: "flex-start" }}
                        spacing={2}
                        sx={{ py: 2.2 }}
                      >
                        <Avatar sx={{ bgcolor: FP.color.navy }}>
                          <AdminPanelSettingsOutlinedIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4 }}>
                            <Typography sx={{ fontWeight: 700 }}>{r.rol}</Typography>
                            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                              {r.usuarios} usuario{r.usuarios === 1 ? "" : "s"} · {r.pantallas}{" "}
                              pantalla{r.pantallas === 1 ? "" : "s"}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                            {r.modulos.map((m) => (
                              <Chip
                                key={m}
                                label={m}
                                size="small"
                                variant="outlined"
                                sx={{ color: FP.color.navy, borderColor: FP.color.navy }}
                              />
                            ))}
                            {r.filaBloqueo && (
                              <Chip
                                icon={<PhonelinkLockOutlinedIcon />}
                                label="Sin celular"
                                size="small"
                                variant="outlined"
                                sx={{ color: "#B3261E", borderColor: "#B3261E" }}
                              />
                            )}
                            {r.desconocidas > 0 && (
                              <Chip
                                label={`Escritorio (${r.desconocidas})`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  color: FP.color.textMuted,
                                  borderColor: FP.color.border,
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexShrink: 0 }}
                        >
                          <Tooltip title="Los usuarios de este rol no podrán abrir la página desde un teléfono. Computadoras, tablets y la app no se afectan.">
                            <FormControlLabel
                              sx={{ mr: 0.5 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={Boolean(r.filaBloqueo)}
                                  disabled={cambiandoBloqueo === r.rol}
                                  onChange={(e) => cambiarBloqueoMovil(r, e.target.checked)}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                                  Bloquear celular
                                </Typography>
                              }
                            />
                          </Tooltip>
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => setRolAEditar(r.rol)}
                            sx={btnOutline(FP.color.navy)}
                          >
                            Editar
                          </Button>
                          <Tooltip
                            title={
                              r.usuarios > 0
                                ? "Reasigne primero los usuarios que tienen este rol"
                                : ""
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={r.usuarios > 0}
                                onClick={() => setEliminandoRolNombre(r.rol)}
                                sx={btnOutline("#B3261E")}
                              >
                                Eliminar
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                      {idx < resumenRoles.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {vistaAvanzada && (
                <>
                  <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        select
                        fullWidth
                        label="Rol"
                        value={filtroRol}
                        onChange={(e) => setFiltroRol(e.target.value)}
                        sx={fpInputSx}
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {rolesDistintos.map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={abrirNuevoRol}
                        sx={btnOutline(FP.color.navy)}
                      >
                        Nueva fila
                      </Button>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={5}
                      md={6}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Typography
                        sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
                      >
                        {filasFiltradas.length} filas
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ bgcolor: "#fff" }}>
                    <DataGrid
                      autoHeight
                      rows={filasFiltradas}
                      columns={columnasRoles}
                      loading={cargandoRoles}
                      rowHeight={46}
                      pageSizeOptions={[25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                      slots={{ noRowsOverlay: NoRowsOverlay }}
                      sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Alta / edición de usuario */}
      <Dialog
        open={editandoUsuario !== null}
        onClose={() => setEditandoUsuario(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editandoUsuario === 0 ? "Nuevo usuario" : "Editar usuario"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Usuario"
                value={formUsuario.nombre}
                disabled={editandoUsuario !== 0}
                onChange={setCampoUsuario("nombre")}
                sx={fpInputSx}
              />
            </Grid>
            {editandoUsuario === 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Clave"
                  value={formUsuario.clave}
                  onChange={setCampoUsuario("clave")}
                  helperText="Mínimo 5 caracteres"
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Rol"
                value={formUsuario.tipo}
                onChange={setCampoUsuario("tipo")}
                sx={fpInputSx}
              >
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={formUsuario.sucursal}
                onChange={setCampoUsuario("sucursal")}
                sx={fpInputSx}
              >
                {/* Si la sucursal guardada ya no está en el catálogo, se
                    ofrece tal cual para no perderla al editar. */}
                {formUsuario.sucursal &&
                  !opcionesSucursal.some((s) => s.codigo === formUsuario.sucursal) && (
                    <MenuItem value={formUsuario.sucursal}>{formUsuario.sucursal}</MenuItem>
                  )}
                {opcionesSucursal.map((s) => (
                  <MenuItem key={s.codigo} value={s.codigo}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Web"
                value={formUsuario.web}
                onChange={setCampoUsuario("web")}
                sx={fpInputSx}
              >
                <MenuItem value="S">Sí</MenuItem>
                <MenuItem value="N">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Local"
                value={formUsuario.local}
                onChange={setCampoUsuario("local")}
                sx={fpInputSx}
              >
                <MenuItem value="S">Sí</MenuItem>
                <MenuItem value="N">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={formUsuario.email}
                onChange={setCampoUsuario("email")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditandoUsuario(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              guardandoUsuario ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
            }
            onClick={guardarUsuario}
            disabled={guardandoUsuario}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cambiar mi clave */}
      <CambiarClaveDialog open={cambiandoClave} onClose={() => setCambiandoClave(false)} />

      {/* Alta / edición de fila de rol */}
      <Dialog
        open={editandoRol !== null}
        onClose={() => setEditandoRol(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editandoRol === 0 ? "Nueva fila de rol" : "Editar fila de rol"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rol"
                value={formRol.rol}
                disabled={editandoRol !== 0}
                onChange={setCampoRol("rol")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Elegir del catálogo rellena descripción y categoría solos;
                  freeSolo permite escribir un programa fuera del catálogo.
                  Se muestra la etiqueta humana, nunca el código ares. */}
              <Autocomplete
                freeSolo
                disabled={editandoRol !== 0}
                options={catalogo}
                value={getEntradaPrograma(formRol.programa) || formRol.programa}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.etiqueta)}
                filterOptions={(opciones, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return opciones;
                  return opciones.filter(
                    (o) =>
                      o.programa.toLowerCase().includes(q) ||
                      o.etiqueta.toLowerCase().includes(q) ||
                      o.modulo.toLowerCase().includes(q)
                  );
                }}
                onChange={(_, valor) => {
                  if (valor && typeof valor === "object") {
                    setFormRol((f) => ({
                      ...f,
                      programa: valor.programa,
                      descripcion: valor.etiqueta,
                      categoria: valor.categoria,
                    }));
                  } else {
                    setFormRol((f) => ({ ...f, programa: valor || "" }));
                  }
                }}
                onInputChange={(_, valor, motivo) => {
                  if (motivo === "input") setFormRol((f) => ({ ...f, programa: valor }));
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.programa}>
                    <Box>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
                        {o.etiqueta}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {o.modulo} · {o.seccion}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Pantalla" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Descripción"
                value={formRol.descripcion}
                onChange={setCampoRol("descripcion")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Categoría"
                value={formRol.categoria}
                onChange={setCampoRol("categoria")}
                sx={fpInputSx}
              />
            </Grid>
            {FLAGS_ROL.map(({ campo, label }) => (
              <Grid item xs={6} sm={3} key={campo}>
                <TextField
                  select
                  fullWidth
                  label={label}
                  value={formRol[campo]}
                  onChange={setCampoRol(campo)}
                  sx={fpInputSx}
                >
                  <MenuItem value="S">Sí</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </TextField>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditandoRol(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={guardandoRol ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardarRol}
            disabled={guardandoRol}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asistente de rol (crear o editar) */}
      <AsistenteRol
        open={asistenteAbierto || rolAEditar !== null}
        onClose={() => {
          setAsistenteAbierto(false);
          setRolAEditar(null);
        }}
        rolesExistentes={[...new Set([...roles, ...rolesDistintos])]}
        filasRoles={filasRoles}
        rolEditar={rolAEditar}
        onGuardado={(rol) => {
          cargarRoles();
          setFiltroRol(rol);
        }}
      />

      {/* Confirmar eliminación de usuario */}
      <Dialog
        open={eliminandoUsuario !== null}
        onClose={() => setEliminandoUsuario(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que desea eliminar el usuario <b>{eliminandoUsuario?.usuario || ""}</b>? Ya no
            podrá entrar al sistema. Los registros que hizo se conservan. Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminandoUsuario(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              borrandoUsuario ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={eliminarUsuario}
            disabled={borrandoUsuario}
            sx={btnSolid("#B3261E", "#8C1D18")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminación de un rol completo */}
      <Dialog
        open={eliminandoRolNombre !== null}
        onClose={() => setEliminandoRolNombre(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar rol</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que desea eliminar el rol <b>{eliminandoRolNombre || ""}</b> con sus{" "}
            <b>
              {filasRoles.filter((f) => f.rol === eliminandoRolNombre).length} pantalla
              {filasRoles.filter((f) => f.rol === eliminandoRolNombre).length === 1 ? "" : "s"}
            </b>
            ? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminandoRolNombre(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              borrandoRolCompleto ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={eliminarRolCompleto}
            disabled={borrandoRolCompleto}
            sx={btnSolid("#B3261E", "#8C1D18")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminación de fila de rol */}
      <Dialog
        open={eliminandoRol !== null}
        onClose={() => setEliminandoRol(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar fila de rol</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que desea eliminar la pantalla{" "}
            <b>
              {eliminandoRol
                ? getEntradaPrograma(eliminandoRol.programa)?.etiqueta || eliminandoRol.programa
                : ""}
            </b>{" "}
            del rol <b>{eliminandoRol ? eliminandoRol.rol : ""}</b>? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminandoRol(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              borrandoRol ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminarRol}
            disabled={borrandoRol}
            sx={btnSolid("#B3261E", "#8C1D18")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
